import { BaseApi } from './base';
import { Invoice, InvoiceFilters } from '../types';
import { supabase } from '../supabase';

export class InvoicesApi extends BaseApi {
  constructor() {
    super('invoices');
  }

  async getInvoices(filters?: InvoiceFilters): Promise<Invoice[]> {
    let query = supabase
      .from('invoices')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.dateRange) {
      query = query.gte('date', filters.dateRange[0]).lte('date', filters.dateRange[1]);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(`Failed to fetch invoices: ${error.message}`);
    }

    return data || [];
  }

  async searchInvoices(searchTerm: string): Promise<Invoice[]> {
    return this.search<Invoice>(searchTerm, ['invoice_no', 'customer_name', 'customer_phone']);
  }

  async getInvoiceStats() {
    const { data: invoices, error } = await supabase
      .from('invoices')
      .select('status, total_amount');

    if (error) {
      throw new Error(`Failed to fetch invoice stats: ${error.message}`);
    }

    const stats = {
      totalInvoices: invoices?.length || 0,
      totalAmount: invoices?.reduce((sum, inv) => sum + inv.total_amount, 0) || 0,
      paidInvoices: invoices?.filter(inv => inv.status === 'paid').length || 0,
      pendingInvoices: invoices?.filter(inv => inv.status === 'sent').length || 0,
      overdueInvoices: invoices?.filter(inv => inv.status === 'overdue').length || 0
    };

    return stats;
  }

  async generateInvoiceNumber(): Promise<string> {
    const { data, error } = await supabase
      .from('invoices')
      .select('invoice_no')
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      throw new Error(`Failed to generate invoice number: ${error.message}`);
    }

    const lastInvoice = data?.[0];
    if (!lastInvoice) {
      return 'INV-2024-0001';
    }

    const lastNumber = parseInt(lastInvoice.invoice_no.split('-').pop() || '0');
    const nextNumber = (lastNumber + 1).toString().padStart(4, '0');
    return `INV-2024-${nextNumber}`;
  }
}

export const invoicesApi = new InvoicesApi();